import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext, useFormState } from 'react-hook-form';
import printJS from 'print-js';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DownloadIcon from '@mui/icons-material/Download';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import MenuItem from '@mui/material/MenuItem';
import { Typography } from '@components/Typography';
import { PopperMenu } from '@components/Popper';
import Button from '@components/Button';
import AddDropdownHeader from '@components/AddDropdownHeader';
import PublicLinkField from '@components/PublicLinkField';
import { surveyService } from '@services';
import { downloadBlobAs } from '@helpers/download';
import { alertActions } from '@actions';
import { useTranslation, useThrottleValue } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';

const PreviewQRContainer = styled('div')({
    position: 'relative',
    width: '100%',
    maxWidth: 384,
    '@media (max-width: 600px)': {
        width: 280,
    },
});

const PreviewQRImg = styled('img')({
    display: 'block',
    width: '100%',
});

const PreviewQRPlaceholder = styled('div')({
    aspectRatio: '1 / 1',
    backgroundColor: '#fff',
});

const LoadingOverlay = styled('div')({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    pointerEvents: 'none',
    '& .MuiCircularProgress-root': {
        color: 'rgba(0, 0, 0, 0.38) !important',
    },
});

const ButtonsWrapper = styled('div')({
    marginTop: 32,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
});

const SurveyLinkWrapper = styled('div')({
    width: '100%',
    maxWidth: 384,
    '@media (max-width: 600px)': {
        maxWidth: 280,
    },
});

const RETRY_DELAYS_MS = [1000, 3000];

const isNetworkError = error => error instanceof TypeError;

const wait = ms =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

const toQrRequest = (qrPreview, extension) => ({ ...qrPreview, logoUrl: qrPreview.qrLogoUrl, extension });

const PreviewCustomization = ({ externalId, locations, brands }) => {
    const [shownQr, setShownQr] = useState(null);
    const [settledPreview, setSettledPreview] = useState(null);
    const requestedPreview = useRef(null);
    const currentPreview = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { control, watch, setValue } = useFormContext();
    const { isValid } = useFormState({ control });
    const { hasLocationAccess, hasBrandAccess } = usePermissions();

    const mainLocationId = watch('mainLocationId');
    const brandId = watch('brandId');
    const locationType = watch('locationType');
    const topText = watch('topText');
    const bottomText = watch('bottomText');
    const backgroundColor = watch('backgroundColor');
    const fillColor = watch('fillColor');
    const qrLogoUrl = watch('qrLogoUrl');
    const customizeQrCodeDesign = watch('customizeQrCodeDesign');
    const followUpEnable = watch('followUpEnable');

    const locationsOptions = useMemo(() => {
        if (!brandId) return [];
        const brandAccessGranted = hasBrandAccess(brandId);
        const availableLocationsIds = brands.find(b => b.id === brandId)?.locations?.map(l => l.id) || [];
        return availableLocationsIds
            .map(id => {
                const location = locations.find(l => l.companyId === id);
                if (!location || !(brandAccessGranted || hasLocationAccess(id))) return undefined;
                return { value: location.companyId, label: location.internalName };
            })
            .filter(Boolean);
    }, [locations, brands, brandId, hasLocationAccess, hasBrandAccess]);

    const locationSurveyId = `${externalId}${locationType === LOCATION_TYPE.BRAND ? `:${mainLocationId}` : ''}`;
    const qrLink = `${import.meta.env.REACT_APP_SURVEY_DOMAIN}/q/${locationSurveyId}`;

    const previewLocationId = locationType === LOCATION_TYPE.BRAND ? mainLocationId : null;
    const preview = useMemo(
        () => ({
            externalId,
            locationId: previewLocationId,
            topText,
            bottomText,
            backgroundColor,
            fillColor,
            qrLogoUrl,
            customizeQrCodeDesign,
            followUpEnable,
        }),
        [
            externalId,
            previewLocationId,
            topText,
            bottomText,
            backgroundColor,
            fillColor,
            qrLogoUrl,
            customizeQrCodeDesign,
            followUpEnable,
        ],
    );
    const throttledPreview = useThrottleValue(preview, 3000);
    const isLoading = isValid && preview !== settledPreview;
    const isShownQrCurrent = shownQr?.preview === preview;

    useEffect(() => {
        currentPreview.current = preview;
        return () => {
            currentPreview.current = null;
        };
    }, [preview]);

    useEffect(() => {
        if (!isShownQrCurrent) setAnchorEl(null);
    }, [isShownQrCurrent]);

    useEffect(() => {
        if (!isValid || throttledPreview !== preview || requestedPreview.current === preview) return;

        requestedPreview.current = preview;
        const isLatest = () => currentPreview.current === preview;
        const fetchImage = async (attempt = 0) => {
            try {
                const image = await surveyService.getSurveyImage(toQrRequest(preview, 'png'));
                if (!isLatest()) {
                    URL.revokeObjectURL(image);
                    return;
                }
                setShownQr({ image, preview });
                setSettledPreview(preview);
            } catch (error) {
                if (!isLatest()) return;
                if (isNetworkError(error) && attempt < RETRY_DELAYS_MS.length) {
                    await wait(RETRY_DELAYS_MS[attempt]);
                    if (isLatest()) fetchImage(attempt + 1);
                    return;
                }
                dispatch(alertActions.error(t('alertMessages.createFail')));
                setSettledPreview(preview);
            }
        };
        fetchImage();
    }, [throttledPreview, preview, isValid]);

    useEffect(() => {
        if (!shownQr) return;
        return () => URL.revokeObjectURL(shownQr.image);
    }, [shownQr]);

    const handleOpen = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const onPrint = () => {
        try {
            printJS({ printable: shownQr.image, type: 'image' });
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.printFail')));
        }
    };

    const onDownload = async ext => {
        try {
            handleClose();
            const extension = ext.toLowerCase();
            const file = await surveyService.getSurveyImage(toQrRequest(shownQr.preview, extension));
            downloadBlobAs(file, `QR.${extension}`);
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.downloadFail')));
        }
    };

    return (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <SurveyLinkWrapper>
                {brandId && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                        <AddDropdownHeader
                            options={locationsOptions}
                            onAdd={id => setValue('mainLocationId', id, { shouldDirty: false })}
                            buttonText={
                                locationsOptions.find(o => o.value === mainLocationId)?.label || t('common.location')
                            }
                            buttonProps={{ size: 'small', sx: { backgroundColor: '#fff' } }}
                        />
                    </div>
                )}
                <PublicLinkField url={qrLink} />
            </SurveyLinkWrapper>
            <PreviewQRContainer>
                {shownQr ? <PreviewQRImg src={shownQr.image} alt="qr" /> : <PreviewQRPlaceholder />}
                {isLoading && (
                    <LoadingOverlay>
                        <CircularProgress />
                    </LoadingOverlay>
                )}
            </PreviewQRContainer>
            <ButtonsWrapper>
                <Button
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                    disabled={!isShownQrCurrent}
                    onClick={handleOpen}
                    startIcon={<DownloadIcon />}
                >
                    {t('buttons.download')}
                </Button>
                <Button
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                    disabled={!isShownQrCurrent}
                    onClick={onPrint}
                    startIcon={<LocalPrintshopIcon />}
                >
                    {t('buttons.print')}
                </Button>
            </ButtonsWrapper>
            {anchorEl && (
                <ClickAwayListener onClickAway={handleClose}>
                    <PopperMenu anchorEl={anchorEl} open={!!anchorEl}>
                        {['PNG', 'SVG', 'PDF'].map(ext => (
                            <MenuItem key={ext} onClick={() => onDownload(ext)}>
                                <Typography variant="body2">{ext}</Typography>
                            </MenuItem>
                        ))}
                    </PopperMenu>
                </ClickAwayListener>
            )}
        </div>
    );
};

export default PreviewCustomization;
